import React, { useEffect, useState } from "react";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import styled from "@mui/material/styles/styled";
import useTheme from "@mui/material/styles/useTheme";

import { misc, neutral } from "app/themes/colors";
import { TagTypes } from "constants/tags";
import { AssignedTag } from "services/types/assigned-tag.type";

import { getAssignedTagTooltip, tagColor } from "./assigned-tags.utils";
import TagChipForm from "./tag-chip-form.component";

type TagChipProps = {
  assignedTag: AssignedTag;
  canEdit?: boolean;
  onDelete?: (tag_id: number) => void;
  tagType: TagTypes;
  disableUpdateMutation?: boolean;
  onUpdate?: (tag: AssignedTag) => void;
  dense?: boolean;
};

const DeleteIcon = styled(HighlightOffRoundedIcon)(() => ({
  "&": {
    fill: misc.blackAsphalt,
    borderRadius: "50%",
    background: misc.default,
    opacity: 0.9
  },
  "&:hover": {
    stroke: misc.grayAsphalt,
    opacity: 1
  }
}));

const checkTagExpiry = (expiredDate: string): boolean =>
  new Date(expiredDate) < new Date();

export default function TagChip({
  assignedTag,
  canEdit,
  onDelete,
  tagType,
  disableUpdateMutation,
  onUpdate,
  dense = false
}: TagChipProps) {
  const { palette } = useTheme();
  const { id, tag, expires_at } = assignedTag;
  const isTagExpired = expires_at ? checkTagExpiry(expires_at) : false;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<any>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Close popover if anchor element becomes invalid or component is about to re-render
  useEffect(() => {
    if (anchorEl && !document.contains(anchorEl)) {
      setAnchorEl(null);
    }
  }, [anchorEl]);

  // Clear anchor on component unmount or assignedTag changes
  useEffect(() => {
    return () => {
      setAnchorEl(null);
    };
  }, [assignedTag.id, assignedTag.description, assignedTag.expires_at]);

  return (
    <>
      <Tooltip
        key={id}
        title={getAssignedTagTooltip(assignedTag, isTagExpired)}
      >
        <Chip
          data-test-id={tag.name}
          size="small"
          label={
            <Stack direction="row" spacing={0.25} alignItems="center">
              {Boolean(expires_at) && (
                <AccessTimeIcon sx={{ width: 14, height: 14 }} />
              )}
              <Box sx={{ pb: 0.2 }}>{tag.name}</Box>
            </Stack>
          }
          variant="outlined"
          deleteIcon={<DeleteIcon htmlColor="#000" viewBox="3 3 18 18" />}
          onDelete={
            onDelete
              ? () => {
                  onDelete(id);
                }
              : undefined
          }
          onClick={canEdit ? handleClick : undefined}
          sx={{
            background: tagColor(tag.color),
            color: palette.getContrastText(tagColor(tag.color)),
            textDecoration: isTagExpired ? "line-through" : "",
            mb: dense ? 0.25 : 0.75,
            mr: dense ? 0.5 : 1,
            border: `solid 1px ${neutral[200]}`,
            fontWeight: 700,
            cursor: canEdit ? "pointer" : undefined,
            ...(dense
              ? {
                  height: "20px",
                  fontSize: "11.5px",
                  "& .MuiChip-label": { px: 0.75 }
                }
              : {}),
            "&: hover": canEdit
              ? {
                  color: "#000"
                }
              : undefined
          }}
        />
      </Tooltip>
      <TagChipForm
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        assignedTag={assignedTag}
        tagType={tagType}
        onUpdate={onUpdate}
        disableUpdateMutation={disableUpdateMutation}
      />
    </>
  );
}
